import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Save } from "lucide-react";
import MultiImageUpload from "@/components/MultiImageUpload";
import QuickAddSelect from "@/components/QuickAddSelect";

const empty = {
  name: "",
  category: "",
  serial: "",
  cost: "",
  purchased_at: "",
  warranty_until: "",
  image_paths: [],
  department: "",
  status: "available",
  notes: "",
  assignee_kind: "",
  assignee_id: "",
  assignee_text: "",
  // v127 stocktake-friendly fields
  location: "",
  brand: "",
  model: "",
  condition: "",
  next_inspection: "",
  po_ref: "",
};

// Nudge messages shown under the category chip — turns the "add" flow into
// a checklist so staff know what to look for without opening a manual.
// Match on partial name so "Tools — Hand" and "Tools — Power" both work.
const CATEGORY_HINTS = {
  ladders: "Look for load rating sticker (kg), height in metres, next-inspection tag.",
  furniture: "Note the room, count of matching items, condition (New/Good/Fair/Worn).",
  racking: "Snap the load-rating plate. Include bay count in the name (e.g. 'Racking Bay A — 4 shelves').",
  shelving: "Snap the load-rating plate. Include bay count in the name (e.g. 'Racking Bay A — 4 shelves').",
  storage: "Size (L/W/H) or capacity (L, kg). Photograph the label.",
  laptop: "Serial on the underside. Note who it's assigned to. Charger goes in Peripherals.",
  desktop: "Serial on the rear label. Note the workstation number.",
  peripheral: "Model number is on the back of monitors, base of docks. Note which desk it's at.",
  network: "Rack unit + port count in the name. Snap the label with MAC address if visible.",
  scanner: "Note the make/model and which department uses it.",
  appliance: "Kitchen/breakroom location. Warranty in months from the box or online.",
  vehicle: "Serial/rego, service due date, last known operator.",
  safety: "Expiry / inspection date is critical here. Snap the tag.",
  ppe: "Expiry / inspection date is critical here. Snap the tag.",
  camera: "Model number, room, connected system (Zoom / Teams / DVR).",
  signage: "Photograph the sign. Note if illuminated (power draw).",
  lighting: "Wattage, room, replacement bulb spec if serviceable.",
  comms: "Extension number, assignee, headset compatibility.",
  cleaning: "Voltage, brand/model, service interval if applicable.",
  ergonom: "Assignee, desk number.",
  tools: "Brand + model + serial. Location (drawer / trolley / bench).",
  it: "Serial, assignee, warranty end. Charger + peripherals as separate assets.",
  general: "Whatever is unusual about this item — put it in Notes so it's findable later.",
};

function hintFor(category) {
  if (!category) return null;
  const c = category.toLowerCase();
  for (const key of Object.keys(CATEGORY_HINTS)) {
    if (c.includes(key)) return CATEGORY_HINTS[key];
  }
  return null;
}

const AssetFormDialog = ({ open, onClose, editing, categories, users, departments, people, onSaved, onCategoriesChanged, onAddPerson }) => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        editing
          ? {
              ...empty,
              ...editing,
              cost: editing.cost ?? "",
              purchased_at: (editing.purchased_at || "").slice(0, 10),
              warranty_until: (editing.warranty_until || "").slice(0, 10),
              assignee_kind: editing.assignee_kind || "",
              assignee_id: editing.assignee_id || "",
              assignee_text: editing.assignee_text || "",
            }
          : empty,
      );
    }
  }, [open, editing]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const payload = {
        ...form,
        cost: form.cost === "" ? null : Number(form.cost),
        purchased_at: form.purchased_at || null,
        warranty_until: form.warranty_until || null,
        next_inspection: form.next_inspection || null,
        category: form.category || null,
        department: form.department || null,
        location: form.location || null,
        brand: form.brand || null,
        model: form.model || null,
        condition: form.condition || null,
        po_ref: form.po_ref || null,
        assignee_kind: form.assignee_kind || null,
        assignee_id:
          form.assignee_kind === "user" ||
          form.assignee_kind === "person" ||
          form.assignee_kind === "dept"
            ? form.assignee_id || null
            : null,
        assignee_text: form.assignee_kind === "text" ? form.assignee_text : null,
      };
      if (editing) {
        await api.put(`/assets/${editing.id}`, payload);
        toast.success("Asset updated");
      } else {
        await api.post("/assets", payload);
        toast.success(`Added "${form.name}"`);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="asset-form-dialog"
      onClick={onClose}
    >
      <div
        className="nb-card w-[min(820px,95vw)] max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Assets</div>
            <h3 className="font-display text-2xl">{editing ? "Edit asset" : "New asset"}</h3>
          </div>
          <button onClick={onClose} className="nb-btn bg-white p-2" data-testid="asset-form-close">
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5 grid gap-4 sm:grid-cols-2">
          <label className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Name *</div>
            <input
              data-testid="asset-name-input"
              className="nb-input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Dell XPS 13 (laptop)"
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Category</div>
            <QuickAddSelect
              testidPrefix="asset-category"
              value={form.category}
              onChange={(v) => set("category", v)}
              options={categories}
              endpoint="/asset-categories"
              placeholder="— None —"
              onCreated={() => onCategoriesChanged?.()}
            />
            {hintFor(form.category) && (
              <div
                data-testid="asset-category-hint"
                className="text-[11px] text-[#5B3A99] bg-[#F1E7FA] border border-[#5B3A99]/30 rounded-md mt-1.5 px-2 py-1"
              >
                💡 {hintFor(form.category)}
              </div>
            )}
          </label>

          <label>
            <div className="label-eyebrow mb-1">Department</div>
            <select
              data-testid="asset-dept-select"
              className="nb-input"
              value={form.department}
              onChange={(e) => set("department", e.target.value)}
            >
              <option value="">— None —</option>
              {departments.map((d) => (
                <option key={d.id} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <div className="label-eyebrow mb-1">Serial / asset tag</div>
            <input
              data-testid="asset-serial-input"
              className="nb-input font-mono-num"
              value={form.serial || ""}
              onChange={(e) => set("serial", e.target.value)}
              placeholder="e.g. AB-1234"
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Status</div>
            <select
              data-testid="asset-status-select"
              className="nb-input"
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
            >
              <option value="available">Available</option>
              <option value="in_use">In use</option>
              <option value="broken_unconfirmed">Broken (unconfirmed)</option>
              <option value="broken">Broken</option>
              <option value="retired">Retired</option>
              <option value="lost">Lost</option>
            </select>
          </label>

          <label>
            <div className="label-eyebrow mb-1">Cost ($)</div>
            <input
              data-testid="asset-cost-input"
              type="number"
              step="0.01"
              className="nb-input font-mono-num"
              value={form.cost}
              onChange={(e) => set("cost", e.target.value)}
              placeholder="0.00"
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Purchased on</div>
            <input
              data-testid="asset-purchased-input"
              type="date"
              className="nb-input"
              value={form.purchased_at || ""}
              onChange={(e) => set("purchased_at", e.target.value)}
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Warranty until</div>
            <input
              data-testid="asset-warranty-input"
              type="date"
              className="nb-input"
              value={form.warranty_until || ""}
              onChange={(e) => set("warranty_until", e.target.value)}
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Brand</div>
            <input
              data-testid="asset-brand-input"
              className="nb-input"
              value={form.brand || ""}
              onChange={(e) => set("brand", e.target.value)}
              placeholder="e.g. Dell, Ryobi, Herman Miller"
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Model</div>
            <input
              data-testid="asset-model-input"
              className="nb-input"
              value={form.model || ""}
              onChange={(e) => set("model", e.target.value)}
              placeholder="e.g. XPS 13 9315, R18AG115"
            />
          </label>

          <label className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Location (where does it live?)</div>
            <input
              data-testid="asset-location-input"
              className="nb-input"
              value={form.location || ""}
              onChange={(e) => set("location", e.target.value)}
              placeholder="e.g. Workshop Bench 2, Racking Bay A, Reception drawer"
            />
          </label>

          <label>
            <div className="label-eyebrow mb-1">Condition</div>
            <select
              data-testid="asset-condition-select"
              className="nb-input"
              value={form.condition || ""}
              onChange={(e) => set("condition", e.target.value)}
            >
              <option value="">— Not set —</option>
              <option value="new">New</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="worn">Worn</option>
              <option value="broken">Broken</option>
            </select>
          </label>

          <label>
            <div className="label-eyebrow mb-1">Next inspection / service</div>
            <input
              data-testid="asset-next-inspection-input"
              type="date"
              className="nb-input"
              value={form.next_inspection || ""}
              onChange={(e) => set("next_inspection", e.target.value)}
              title="For safety/PPE, ladders, racking — anything with an inspection cycle"
            />
          </label>

          <label className="sm:col-span-2">
            <div className="label-eyebrow mb-1">PO / Invoice reference</div>
            <input
              data-testid="asset-po-ref-input"
              className="nb-input"
              value={form.po_ref || ""}
              onChange={(e) => set("po_ref", e.target.value)}
              placeholder="e.g. PO-2026-0013, Bunnings TXN 4487281"
            />
          </label>

          <div className="sm:col-span-2 border-2 border-black rounded-xl p-3 bg-[#F4F8FB]">
            <div className="label-eyebrow mb-2">Assigned to</div>
            <div className="flex flex-wrap gap-3">
              {[
                { k: "", lbl: "Unassigned" },
                { k: "user", lbl: "A login user" },
                { k: "person", lbl: "A staff member (no login)" },
                { k: "dept", lbl: "A department" },
                { k: "text", lbl: "Free text" },
              ].map((o) => (
                <label key={o.k || "none"} className="flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    data-testid={`asset-assignee-kind-${o.k || "none"}`}
                    checked={form.assignee_kind === o.k}
                    onChange={() => {
                      set("assignee_kind", o.k);
                      set("assignee_id", "");
                      set("assignee_text", "");
                    }}
                  />
                  {o.lbl}
                </label>
              ))}
            </div>
            {form.assignee_kind === "user" && (
              <select
                data-testid="asset-assignee-user-select"
                className="nb-input mt-2"
                value={form.assignee_id || ""}
                onChange={(e) => set("assignee_id", e.target.value)}
              >
                <option value="">— Pick a login user —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            )}
            {form.assignee_kind === "person" && (
              <div className="flex flex-col gap-2 mt-2">
                <select
                  data-testid="asset-assignee-person-select"
                  className="nb-input"
                  value={form.assignee_id || ""}
                  onChange={(e) => set("assignee_id", e.target.value)}
                >
                  <option value="">— Pick a staff member —</option>
                  {(people || [])
                    .filter((p) => p.active !== false)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                        {p.role ? ` · ${p.role}` : ""}
                        {p.department ? ` · ${p.department}` : ""}
                      </option>
                    ))}
                </select>
                {onAddPerson && (
                  <button
                    type="button"
                    data-testid="asset-assignee-add-person"
                    onClick={() =>
                      onAddPerson((created) => {
                        // Auto-select the just-created person so the manager
                        // doesn't have to hunt for them in the dropdown.
                        set("assignee_id", created?.id || "");
                      })
                    }
                    className="nb-btn bg-[#DFEEF7] border-[#0A3A7A] text-[#0A3A7A] px-3 py-1.5 text-xs self-start"
                  >
                    + Add new staff member…
                  </button>
                )}
              </div>
            )}
            {form.assignee_kind === "dept" && (
              <select
                data-testid="asset-assignee-dept-select"
                className="nb-input mt-2"
                value={form.assignee_id || ""}
                onChange={(e) => set("assignee_id", e.target.value)}
              >
                <option value="">— Pick a department —</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            )}
            {form.assignee_kind === "text" && (
              <input
                data-testid="asset-assignee-text-input"
                className="nb-input mt-2"
                value={form.assignee_text || ""}
                onChange={(e) => set("assignee_text", e.target.value)}
                placeholder="e.g. Stephanie (Reception), Workshop bench 2"
              />
            )}
          </div>

          <label className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Notes</div>
            <textarea
              data-testid="asset-notes-input"
              className="nb-input min-h-[60px]"
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="USB-C iPad Pro compatibility, service history, login PIN, etc."
            />
          </label>

          <div className="sm:col-span-2">
            <div className="label-eyebrow mb-1">Photos</div>
            <MultiImageUpload
              paths={form.image_paths || []}
              onChange={(paths) => set("image_paths", paths)}
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white px-5 py-4 border-t-2 border-black flex justify-end gap-2">
          <button onClick={onClose} className="nb-btn bg-white px-4 py-2">
            Cancel
          </button>
          <button
            data-testid="asset-form-save"
            onClick={submit}
            disabled={busy || !form.name.trim()}
            className="nb-btn nb-btn-primary px-4 py-2 flex items-center gap-1"
          >
            <Save size={14} strokeWidth={2.5} /> Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetFormDialog;
